// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { IconX } from '@tabler/icons-react';
import cx from 'clsx';
import dayjs from 'dayjs';
import type { JSX, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type AvailabilityBlock,
  type AvailabilityMode,
  DAY_OF_WEEK_NAMES,
  formatMinutesLabel,
  GRID_END_HOUR,
  GRID_START_HOUR,
  PX_PER_MINUTE,
} from './types';
import classes from './WeeklyAvailabilityGrid.module.css';

const GRID_MIN_MINUTES = GRID_START_HOUR * 60;
const GRID_MAX_MINUTES = GRID_END_HOUR * 60;
const GRID_HEIGHT_PX = (GRID_MAX_MINUTES - GRID_MIN_MINUTES) * PX_PER_MINUTE;
const SNAP_MINUTES = 15;
const MIN_BLOCK_DURATION_MINUTES = 15;
const DEFAULT_BLOCK_DURATION_MINUTES = 60;

interface WeeklyAvailabilityGridProps {
  mode: AvailabilityMode;
  weekStart: Date;
  blocks: AvailabilityBlock[];
  isEditing: boolean;
  onChange?: (blocks: AvailabilityBlock[]) => void;
}

type DragState =
  | { kind: 'create'; dayIndex: number; anchorMin: number; currentMin: number }
  | {
      kind: 'resize-top';
      blockId: string;
      originalStart: number;
      originalEnd: number;
      currentStart: number;
    }
  | {
      kind: 'resize-bottom';
      blockId: string;
      originalStart: number;
      originalEnd: number;
      currentEnd: number;
    }
  | {
      kind: 'move';
      blockId: string;
      originalDayIndex: number;
      originalStart: number;
      originalEnd: number;
      anchorMin: number;
      anchorDayIndex: number;
      currentDayIndex: number;
      currentStart: number;
      currentEnd: number;
    };

function newBlockId(): string {
  return `block-${Math.random().toString(36).slice(2, 10)}`;
}

function snapToGrid(minutes: number): number {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

function clampMinutes(minutes: number): number {
  return Math.max(GRID_MIN_MINUTES, Math.min(GRID_MAX_MINUTES, minutes));
}

function mergeOverlappingBlocks(blocks: AvailabilityBlock[]): AvailabilityBlock[] {
  const byDay = new Map<number, AvailabilityBlock[]>();
  for (const b of blocks) {
    const list = byDay.get(b.dayIndex) ?? [];
    list.push(b);
    byDay.set(b.dayIndex, list);
  }
  const out: AvailabilityBlock[] = [];
  for (const [, dayBlocks] of byDay) {
    dayBlocks.sort((a, b) => a.startMinutes - b.startMinutes);
    let current: AvailabilityBlock | null = null;
    for (const b of dayBlocks) {
      if (!current) {
        current = { ...b };
        continue;
      }
      if (b.startMinutes <= current.endMinutes) {
        current.endMinutes = Math.max(current.endMinutes, b.endMinutes);
      } else {
        out.push(current);
        current = { ...b };
      }
    }
    if (current) {
      out.push(current);
    }
  }
  return out;
}

function applyDragToBlocks(blocks: AvailabilityBlock[], drag: DragState | null): AvailabilityBlock[] {
  if (!drag) {
    return blocks;
  }
  if (drag.kind === 'create') {
    const start = Math.min(drag.anchorMin, drag.currentMin);
    const end = Math.max(drag.anchorMin, drag.currentMin);
    if (end - start < MIN_BLOCK_DURATION_MINUTES) {
      return [
        ...blocks,
        {
          id: '__draft__',
          dayIndex: drag.dayIndex,
          startMinutes: start,
          endMinutes: start + MIN_BLOCK_DURATION_MINUTES,
        },
      ];
    }
    return [...blocks, { id: '__draft__', dayIndex: drag.dayIndex, startMinutes: start, endMinutes: end }];
  }
  return blocks.map((b) => {
    if (drag.kind === 'resize-top' && b.id === drag.blockId) {
      return { ...b, startMinutes: drag.currentStart };
    }
    if (drag.kind === 'resize-bottom' && b.id === drag.blockId) {
      return { ...b, endMinutes: drag.currentEnd };
    }
    if (drag.kind === 'move' && b.id === drag.blockId) {
      return {
        ...b,
        dayIndex: drag.currentDayIndex,
        startMinutes: drag.currentStart,
        endMinutes: drag.currentEnd,
      };
    }
    return b;
  });
}

export function WeeklyAvailabilityGrid({
  mode,
  weekStart,
  blocks,
  isEditing,
  onChange,
}: WeeklyAvailabilityGridProps): JSX.Element {
  const bodyRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragStateRef = useRef<DragState | null>(null);

  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  const pointerYToMinutes = useCallback((clientY: number): number => {
    const body = bodyRef.current;
    if (!body) {
      return GRID_MIN_MINUTES;
    }
    const rect = body.getBoundingClientRect();
    const offsetY = clientY - rect.top;
    const minutes = GRID_MIN_MINUTES + offsetY / PX_PER_MINUTE;
    return clampMinutes(snapToGrid(minutes));
  }, []);

  const pointerXToDayIndex = useCallback((clientX: number): number | null => {
    for (let i = 0; i < columnRefs.current.length; i++) {
      const el = columnRefs.current[i];
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX < rect.right) {
        return i;
      }
    }
    return null;
  }, []);

  const commit = useCallback(
    (next: AvailabilityBlock[]) => {
      if (!onChange) {
        return;
      }
      const cleaned = next
        .filter((b) => b.id !== '__draft__')
        .filter((b) => b.endMinutes - b.startMinutes >= MIN_BLOCK_DURATION_MINUTES)
        .map((b) => ({
          ...b,
          startMinutes: clampMinutes(b.startMinutes),
          endMinutes: clampMinutes(b.endMinutes),
        }));
      onChange(mergeOverlappingBlocks(cleaned));
    },
    [onChange]
  );

  useEffect(() => {
    if (!dragState) {
      return;
    }

    const handleMove = (e: PointerEvent): void => {
      const ds = dragStateRef.current;
      if (!ds) {
        return;
      }
      const minutes = pointerYToMinutes(e.clientY);

      if (ds.kind === 'create') {
        setDragState({ ...ds, currentMin: minutes });
        return;
      }
      if (ds.kind === 'resize-top') {
        const next = Math.min(minutes, ds.originalEnd - MIN_BLOCK_DURATION_MINUTES);
        setDragState({ ...ds, currentStart: clampMinutes(next) });
        return;
      }
      if (ds.kind === 'resize-bottom') {
        const next = Math.max(minutes, ds.originalStart + MIN_BLOCK_DURATION_MINUTES);
        setDragState({ ...ds, currentEnd: clampMinutes(next) });
        return;
      }
      if (ds.kind === 'move') {
        const dayIdx = pointerXToDayIndex(e.clientX);
        const delta = minutes - ds.anchorMin;
        let nextStart = ds.originalStart + delta;
        let nextEnd = ds.originalEnd + delta;
        const duration = ds.originalEnd - ds.originalStart;
        if (nextStart < GRID_MIN_MINUTES) {
          nextStart = GRID_MIN_MINUTES;
          nextEnd = nextStart + duration;
        }
        if (nextEnd > GRID_MAX_MINUTES) {
          nextEnd = GRID_MAX_MINUTES;
          nextStart = nextEnd - duration;
        }
        setDragState({
          ...ds,
          currentDayIndex: dayIdx ?? ds.currentDayIndex,
          currentStart: nextStart,
          currentEnd: nextEnd,
        });
      }
    };

    const handleUp = (): void => {
      const ds = dragStateRef.current;
      setDragState(null);
      if (!ds) {
        return;
      }
      if (ds.kind === 'create') {
        const rawStart = Math.min(ds.anchorMin, ds.currentMin);
        const rawEnd = Math.max(ds.anchorMin, ds.currentMin);
        const start = clampMinutes(rawStart);
        const end =
          rawEnd - rawStart < MIN_BLOCK_DURATION_MINUTES
            ? clampMinutes(start + DEFAULT_BLOCK_DURATION_MINUTES)
            : clampMinutes(rawEnd);
        commit([...blocks, { id: newBlockId(), dayIndex: ds.dayIndex, startMinutes: start, endMinutes: end }]);
        return;
      }
      if (ds.kind === 'resize-top') {
        commit(blocks.map((b) => (b.id === ds.blockId ? { ...b, startMinutes: ds.currentStart } : b)));
        return;
      }
      if (ds.kind === 'resize-bottom') {
        commit(blocks.map((b) => (b.id === ds.blockId ? { ...b, endMinutes: ds.currentEnd } : b)));
        return;
      }
      if (ds.kind === 'move') {
        commit(
          blocks.map((b) =>
            b.id === ds.blockId
              ? {
                  ...b,
                  dayIndex: ds.currentDayIndex,
                  startMinutes: ds.currentStart,
                  endMinutes: ds.currentEnd,
                }
              : b
          )
        );
      }
    };

    const handleCancel = (): void => {
      setDragState(null);
    };

    const handleKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setDragState(null);
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [dragState, blocks, commit, pointerYToMinutes, pointerXToDayIndex]);

  const handleDayPointerDown =
    (dayIndex: number) =>
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!isEditing || e.button !== 0) {
        return;
      }
      const minutes = pointerYToMinutes(e.clientY);
      setDragState({ kind: 'create', dayIndex, anchorMin: minutes, currentMin: minutes });
    };

  const handleResizeTopPointerDown =
    (block: AvailabilityBlock) =>
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!isEditing || e.button !== 0) {
        return;
      }
      e.stopPropagation();
      setDragState({
        kind: 'resize-top',
        blockId: block.id,
        originalStart: block.startMinutes,
        originalEnd: block.endMinutes,
        currentStart: block.startMinutes,
      });
    };

  const handleResizeBottomPointerDown =
    (block: AvailabilityBlock) =>
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!isEditing || e.button !== 0) {
        return;
      }
      e.stopPropagation();
      setDragState({
        kind: 'resize-bottom',
        blockId: block.id,
        originalStart: block.startMinutes,
        originalEnd: block.endMinutes,
        currentEnd: block.endMinutes,
      });
    };

  const handleBlockPointerDown =
    (block: AvailabilityBlock) =>
    (e: ReactPointerEvent<HTMLDivElement>): void => {
      if (!isEditing || e.button !== 0) {
        return;
      }
      e.stopPropagation();
      const minutes = pointerYToMinutes(e.clientY);
      setDragState({
        kind: 'move',
        blockId: block.id,
        originalDayIndex: block.dayIndex,
        originalStart: block.startMinutes,
        originalEnd: block.endMinutes,
        anchorMin: minutes,
        anchorDayIndex: block.dayIndex,
        currentDayIndex: block.dayIndex,
        currentStart: block.startMinutes,
        currentEnd: block.endMinutes,
      });
    };

  const handleDelete =
    (block: AvailabilityBlock) =>
    (e: ReactMouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      if (!onChange) {
        return;
      }
      setDragState(null);
      commit(blocks.filter((b) => b.id !== block.id));
    };

  const displayBlocks = applyDragToBlocks(blocks, dragState);
  const start = dayjs(weekStart);

  const hourLabels: number[] = [];
  for (let h = GRID_START_HOUR; h <= GRID_END_HOUR; h++) {
    hourLabels.push(h);
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <div className={classes.headerCellEmpty} />
        {DAY_OF_WEEK_NAMES.map((name, idx) => {
          const date = start.add(idx, 'day');
          return (
            <div key={name} className={classes.headerCell}>
              <div className={classes.dayName}>{mode === 'specific' ? name : `${name}s`}</div>
              {mode === 'specific' && <div className={classes.dayDate}>{date.format('MMMM D, YYYY')}</div>}
            </div>
          );
        })}
      </div>
      <div ref={bodyRef} className={classes.bodyRow} style={{ height: GRID_HEIGHT_PX }}>
        <div className={classes.timeColumn}>
          {hourLabels.map((h) => {
            const top = (h - GRID_START_HOUR) * 60 * PX_PER_MINUTE;
            return (
              <div
                key={h}
                className={classes.timeCell}
                style={{ position: 'absolute', top: top - 6, left: 0, width: 60 }}
              >
                {formatMinutesLabel(h * 60)}
              </div>
            );
          })}
        </div>
        {DAY_OF_WEEK_NAMES.map((name, dayIndex) => (
          <div
            key={name}
            ref={(el) => {
              columnRefs.current[dayIndex] = el;
            }}
            className={cx(classes.dayColumn, { [classes.editable]: isEditing })}
            onPointerDown={isEditing ? handleDayPointerDown(dayIndex) : undefined}
            aria-label={isEditing ? `Drag to add availability on ${name}` : undefined}
          >
            {hourLabels.map((h) => {
              const top = (h - GRID_START_HOUR) * 60 * PX_PER_MINUTE;
              return <div key={h} className={classes.hourLine} style={{ top }} />;
            })}
            {displayBlocks
              .filter((b) => b.dayIndex === dayIndex)
              .map((block) => {
                const top = (block.startMinutes - GRID_MIN_MINUTES) * PX_PER_MINUTE;
                const height =
                  Math.max(MIN_BLOCK_DURATION_MINUTES, block.endMinutes - block.startMinutes) * PX_PER_MINUTE;
                const isDraft = block.id === '__draft__';
                const isDragging =
                  dragState !== null &&
                  ((dragState.kind !== 'create' && 'blockId' in dragState && dragState.blockId === block.id) ||
                    (dragState.kind === 'create' && isDraft));
                return (
                  <div
                    key={block.id}
                    className={cx(classes.block, {
                      [classes.editable]: isEditing && !isDraft,
                      [classes.dragging]: isDragging,
                      [classes.draft]: isDraft,
                    })}
                    style={{ top, height }}
                    onPointerDown={isEditing && !isDraft ? handleBlockPointerDown(block) : undefined}
                    aria-label={`Availability ${formatMinutesLabel(block.startMinutes)} to ${formatMinutesLabel(block.endMinutes)}`}
                  >
                    {isEditing && !isDraft && (
                      <div
                        className={cx(classes.resizeHandle, classes.resizeHandleTop)}
                        onPointerDown={handleResizeTopPointerDown(block)}
                        aria-label="Resize start time"
                        role="separator"
                      />
                    )}
                    <span className={classes.blockLabelTop}>{formatMinutesLabel(block.startMinutes)}</span>
                    {isEditing && !isDraft && (
                      <button
                        type="button"
                        aria-label="Remove block"
                        className={classes.blockDelete}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={handleDelete(block)}
                      >
                        <IconX size={12} />
                      </button>
                    )}
                    <span className={classes.blockLabelBottom}>{formatMinutesLabel(block.endMinutes)}</span>
                    {isEditing && !isDraft && (
                      <div
                        className={cx(classes.resizeHandle, classes.resizeHandleBottom)}
                        onPointerDown={handleResizeBottomPointerDown(block)}
                        aria-label="Resize end time"
                        role="separator"
                      />
                    )}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}
